import { addMonths, format, formatISO, parseISO, subMonths } from "date-fns"
import { Package } from "../trips/Package"
import { isDefined, isNonEmptyString, reduceErrors, validate, ValidationErrors } from "@util/validations"
export interface Milestone {
  id: number
  tripId: number
  date?: string
  type: "fixed" | "percentage"
  amount?: number
  isEditing: boolean
}

export function toggleType(milestone: Milestone) {
  if (milestone.type === "fixed") {
    milestone.type = "percentage"
  } else {
    milestone.type = "fixed"
  }

  return milestone
}

export function formatPercentage(milestones: Milestone[]) {
  const unroundedPercentage = 100 / percentageMilestones(milestones).length
  return Math.round(unroundedPercentage * 100) / 100
}

function percentageMilestones(milestones: Milestone[]) {
  return milestones.filter((m) => m.type === "percentage")
}

export function formatDate(milestone: Milestone) {
  if (milestone.date) {
    return format(parseISO(milestone.date), "MMM dd yyyy")
  } else {
    return "Date Required"
  }
}

export function isFixed(milestone: Milestone): boolean {
  return milestone.type === "fixed"
}

export function isPercentage(milestone: Milestone): boolean {
  return milestone.type === "percentage"
}

export function percentageAmount(milestones: Milestone[], milestone: Milestone, pkg: Package) {
  if (milestone.type === "fixed") {
    return milestone.amount
  }
  const fixedTotal = milestones.filter((m) => m.type === "fixed").reduce((acc, m) => acc + m.amount, 0)
  const percentageTotal = Math.max(pkg.price - fixedTotal, 0)

  const unroundedPercentagePrice = (percentageTotal * formatPercentage(milestones)) / 100
  const roundedPercentagePrice = Math.round(unroundedPercentagePrice / 100) * 100

  if (isLastPercentageMilestone(milestones, milestone)) {
    return percentageTotal - roundedPercentagePrice * (milestones.filter((m) => m.type === "percentage").length - 1)
  } else {
    return roundedPercentagePrice
  }
}

export function validateMilestone(data: Milestone): ValidationErrors<Milestone> {
  if (data.type === "fixed") {
    return reduceErrors({
      date: [validate(isNonEmptyString(data.date), "You must enter a date for this payment")],
      amount: [validate(isDefined(data.amount), "You must enter a valid amount")],
    })
  } else {
    return reduceErrors({
      date: [validate(isNonEmptyString(data.date), "You must enter a date for this payment")],
    })
  }
}

export function addMilestone(milestones: Milestone[], tripId: number, strategy: LateRegistrantPaymentStrategy = "catch_up") {
  if (milestones.length < 2) {
    let newDate = formatISO(new Date(), { representation: "date" })
    if (milestones.length == 1) {
      // When there's only 1 milestone (typically the final payment), add the new one BEFORE it
      // so it can serve as a deposit
      newDate = milestones[0].date && formatISO(subMonths(parseISO(milestones[0].date), 1), { representation: "date" })
    }

    // For distribute strategy, check if this new milestone would be first by date
    const canBeFixed = canAddFixedMilestoneAtDate(milestones, newDate, strategy)
    const type = canBeFixed ? "fixed" : "percentage"

    milestones.splice(0, 0, {
      date: newDate,
      type: type,
      amount: type === "fixed" ? 50_00 : undefined,
      tripId: tripId,
      isEditing: true,
      id: undefined,
    } as Milestone)
  } else {
    const previous = milestones[milestones.length - 2]
    const newType = strategy === "distribute" ? "percentage" : previous.type

    milestones.splice(milestones.length - 1, 0, {
      date: previous.date && formatISO(addMonths(parseISO(previous.date), 1), { representation: "date" }),
      type: newType,
      amount: newType === "fixed" ? previous.amount : undefined,
      tripId: previous.tripId,
      isEditing: true,
      id: undefined,
    })
  }

  return milestones
}

export function isSolePersistedPercentageMilestone(milestones: Milestone[], milestone: Milestone) {
  if (milestone.type === "fixed") {
    return false
  } else if (milestone.id === undefined) {
    return false
  } else {
    const persistedPercentageMilestones = milestones.filter((m) => m.type === "percentage").filter((m) => m.id !== undefined)
    return persistedPercentageMilestones.length === 1 && persistedPercentageMilestones[0] === milestone
  }
}

function isLastPercentageMilestone(milestones: Milestone[], milestone: Milestone): boolean {
  const percentageMilestones = milestones.filter((m) => m.type === "percentage")
  return percentageMilestones.indexOf(milestone) == percentageMilestones.length - 1
}

export function sortMilestonesByDate(milestones: Milestone[]): Milestone[] {
  return [...milestones].sort((a, b) => {
    if (!a.date || !b.date) return 0
    return new Date(a.date).getTime() - new Date(b.date).getTime()
  })
}

export type LateRegistrantPaymentStrategy = "catch_up" | "distribute"

export function canMilestoneBeFixed(
  milestones: Milestone[],
  index: number,
  strategy: LateRegistrantPaymentStrategy,
): boolean {
  if (strategy !== "distribute") {
    return true
  }

  if (milestones.length <= 1) {
    return true
  }

  const sortedByDate = sortMilestonesByDate(milestones)
  const milestone = milestones[index]
  const dateOrderIndex = sortedByDate.findIndex((m) => (milestone.id ? m.id === milestone.id : m === milestone))
  return dateOrderIndex === 0
}

export function canToggleToFixed(
  milestones: Milestone[],
  milestone: Milestone,
  strategy: LateRegistrantPaymentStrategy,
): boolean {
  if (strategy !== "distribute") {
    return true
  }

  if (milestones.length <= 1) {
    return true
  }

  const sortedByDate = sortMilestonesByDate(milestones)
  const dateOrderIndex = sortedByDate.findIndex((m) => (milestone.id ? m.id === milestone.id : m === milestone))
  return dateOrderIndex === 0
}

export function hasFixedMilestoneAtFirstPosition(milestones: Milestone[]): boolean {
  if (milestones.length === 0) {
    return false
  }

  const sortedByDate = sortMilestonesByDate(milestones)
  return sortedByDate[0].type === "fixed"
}

export function canAddFixedMilestone(milestones: Milestone[], strategy: LateRegistrantPaymentStrategy): boolean {
  if (strategy !== "distribute") {
    return true
  }

  if (milestones.length === 0) {
    return true
  }

  return !hasFixedMilestoneAtFirstPosition(milestones)
}

export function canAddFixedMilestoneAtDate(
  milestones: Milestone[],
  proposedDate: string,
  strategy: LateRegistrantPaymentStrategy,
): boolean {
  if (strategy !== "distribute") {
    return true
  }

  if (milestones.length === 0) {
    return true
  }

  // Check if any existing milestone already has a fixed type
  const existingFixed = milestones.find((m) => m.type === "fixed")
  if (existingFixed) {
    // There's already a fixed milestone - new one can't be fixed
    return false
  }

  // Check if the proposed date would be first by date order
  const sortedByDate = sortMilestonesByDate(milestones)
  const firstExistingDate = sortedByDate[0].date
  if (!firstExistingDate || !proposedDate) {
    return true
  }

  // New milestone can be fixed only if its date is before all existing milestones
  return new Date(proposedDate).getTime() < new Date(firstExistingDate).getTime()
}
